import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchInfoCapitalIssuesApp, updateInformationCapitalIssue,
  fetchBusinessProcesses,
  fetchInfoCapitalGoalsApp,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmInfoCapitalIssueAppEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedBusinessProcessId, setSelectedBusinessProcessId] = useState("");
  const [sourceIds, setSourceIds] = useState([]);
  const [selectedIndicatorNames, setSelectedIndicatorNames] = useState([]);
  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [fullData, setFullData] = useState(null);

  const availableIndicators = [...new Map(
    sourceIds
      .flatMap((id) => {
        const goal = infoCapitalGoalsApp.find((g) => g.successFactorId === id);
        return goal?.indicators || [];
      })
      .map((ind) => [ind.indicatorName, ind])
  ).values()];

  useEffect(() => {
    (async () => {
      try {
        const [issues, bps, icGoals] = await Promise.all([
          fetchInfoCapitalIssuesApp(businessDomainId),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
        ]);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setInfoCapitalGoalsApp(Array.isArray(icGoals) ? icGoals : []);
        const found = (Array.isArray(issues) ? issues : []).find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSelectedBusinessProcessId(found.businessProcessId || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedIndicatorNames((found.indicators || []).map((ind) => ind.indicatorName));
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleSourceChange = (e) => {
    setSourceIds(e.target.value);
    setSelectedIndicatorNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const sources = sourceIds
        .map((id) => {
          const goal = infoCapitalGoalsApp.find((g) => g.successFactorId === id);
          if (!goal) return null;
          return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
        })
        .filter(Boolean);
      const indicators = selectedIndicatorNames
        .map((name) => availableIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      await updateInformationCapitalIssue({
        ...fullData,
        successFactorName,
        successFactorDescription,
        businessProcessId: selectedBusinessProcessId,
        tasks: fullData.tasks || [],
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-issues-app/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("infoCapitalIssueApp.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("businessProcess.title")}</InputLabel>
              <Select
                value={selectedBusinessProcessId}
                label={t("businessProcess.title")}
                onChange={(e) => setSelectedBusinessProcessId(e.target.value)}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {businessProcesses.map((bp) => (
                  <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                    {bp.businessProcessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={handleSourceChange}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {infoCapitalGoalsApp.map((item) => (
                  <MenuItem key={item.successFactorId} value={item.successFactorId}>
                    {item.successFactorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={availableIndicators.length === 0}
                value={selectedIndicatorNames}
                onChange={(e) => setSelectedIndicatorNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {availableIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                    {ind.indicatorName}（{ind.unit}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-issues-app/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
